import PropTypes from 'prop-types'
import { ReactComponent as CommentIcon } from '../../assets/images/common/icons/comment-icon.svg'
import { ReactComponent as Checkmark } from '../../assets/images/common/icons/checkmark-icon.svg'
import { ReactComponent as DownArrow } from '../../assets/images/common/icons/richTextEditor/chevron-down.svg'
import moment from 'moment'
import classes from './ActivityAnnouncementCard.module.scss'
import ImageWithOverlay from './ImageWithOverlay'
import { pluralize } from '../../utility/helperFunctions'
import { useState, useEffect, useRef } from 'react'
import ActivityAnnouncementModal from './modals/ActivityAnnouncementModal'
import CoachHeaderInfo from './CoachHeaderInfo'
import { Popover } from 'reactstrap'
import ConfirmationModal from '../common/ConfirmationModal'
import { requestDeleteActivity } from '../../actions/activityActions'
import { toast } from 'react-toastify'
import { useDispatch } from 'react-redux'
import PostAnnouncementModal from '../dashboard/coachDashboard/PostAnnouncementModal'
import { resetSelectedData } from '../../reducers/programDashboardReducer'
import DOMPurify from 'dompurify'
import DocumentCard from '../dashboard/coachDashboard/DocumentCard'
import { useProgramsText } from '../dashboard/coachDashboard/useProgramsText'

const ActivityAnnouncement = ({ activity, removeActivity, isProgram }) => {
  const fileUrl = activity.photo?.original_file_url
  const videoUrl = activity.video?.attachment_url
  const dispatch = useDispatch()
  const [modalOpen, setModalOpen] = useState(false)
  const [dropdown, setDropdown] = useState(false)
  const [confirmationModal, setConfirmationModal] = useState(false)
  const [editModal, setEditModal] = useState(false)
  const [isPortrait, setIsPortrait] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [shouldTruncate, setShouldTruncate] = useState(false)

  useEffect(() => {
    if (fileUrl) {
      const img = new Image()
      img.src = fileUrl
      img.onload = () => {
        setIsPortrait(img.height > img.width)
      }
    } else if (videoUrl) {
      const video = document.createElement('video')
      video.src = videoUrl
      video.addEventListener('loadedmetadata', () => {
        setIsPortrait(video.videoHeight > video.videoWidth)
      })
    }
  }, [fileUrl, videoUrl])

  const buttonRef = useRef(null)

  const openModal = () => {
    setModalOpen(true)
  }

  const onClose = () => {
    setModalOpen(false)
  }

  const openDropdown = (e) => {
    e.stopPropagation()
    setDropdown(true)
  }

  const onDeletePress = (e) => {
    e.stopPropagation()
    setDropdown(false)
    setConfirmationModal(true)
  }

  const onEditPress = (e) => {
    e.stopPropagation()
    setDropdown(false)
    setEditModal(true)
  }

  const handleCloseEditModal = () => {
    setEditModal(false)
    dispatch(resetSelectedData())
  }

  const closeConfirmationModal = () => {
    setConfirmationModal(false)
  }

  const deleteAnnouncement = () => {
    requestDeleteActivity(activity).then((response) => {
      if (response.status === 201) {
        toast.success('Announcement deleted successfully', { position: toast.POSITION.TOP_RIGHT })
        dispatch(removeActivity(activity.id))
      } else {
        toast.error('Something went wrong. Please try again.', { position: toast.POSITION.TOP_RIGHT })
      }
      setConfirmationModal(false)
    })
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target)

      ) {
        setDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    const cleanText = DOMPurify.sanitize(activity.caption, { ADD_ATTR: ['target'] })
    setShouldTruncate(cleanText.length > 400)
  }, [activity.caption])


  const programsText = useProgramsText({
    selectedPrograms: activity.courses,
    selectedSeries: activity.series,
    selectedUsers: activity.users,
  })

  const confirmationMesssage = `Are you sure you want to delete ${activity.user.first_name} ${activity.user.last_name}'s announcement?`

  return (
    <>
      <div className={classes.container} onClick={openModal} role='button' tabIndex={0} onKeyPress={openModal}>
        <button onClick={openDropdown} id={`Popover${activity.id}`} ref = {buttonRef} className={classes.dropdownButton}>
          <DownArrow />
        </button>
        <Popover
          placement='bottom'
          isOpen={dropdown}
          target={`Popover${activity.id}`}
          toggle={setDropdown}
        >
          <div className='activity-dropdown-container'>
            <button
              className='btn btn-link activity-dropdown-delete'
              onClick={onDeletePress}
            >
              {'Delete'}
            </button>
            <button
              className='btn btn-link activity-dropdown-delete'
              onClick={onEditPress}
            >
              {'Edit'}
            </button>
          </div>
        </Popover>
        <div className={classes.coachHeader}>
          <CoachHeaderInfo coach={activity.user} />
        </div>

        {activity.title && <div className={classes.title}>
          {activity.title}
        </div>}

        <div className={classes.caption}>
          <div
            role='button'
            tabIndex={0}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation()
            }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                shouldTruncate && !isExpanded
                  ? activity.caption.slice(0, 400) + '.....'
                  : activity.caption
                , { ADD_ATTR: ['target'] }),
            }}
          />
          {shouldTruncate && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                setIsExpanded((prev) => !prev)
              }}
              className={classes.readMoreButton}
            >
              {isExpanded ? 'View Less' : 'View More'}
            </button>
          )}
        </div>
        <span className={classes.fromNow}>{moment(activity.activity_date).fromNow()}</span>
        {activity.document && (
          <div className={classes.documentContainer}>
            <a
              href={activity.document.url}
              target='_blank'
              rel='noopener noreferrer'
              onClick={(e) => e.stopPropagation()}
            >
              <DocumentCard
                name={activity.document.name}
                size={(activity.document.size / 1024).toFixed(1)}
              />
            </a>
          </div>
        )}
        <div className={classes.imageContainer}>
          {(fileUrl || videoUrl) && <ImageWithOverlay alt={programsText} height={isPortrait ? '60vh' : '30vh'}
            video={videoUrl} src={fileUrl} imgStyle='cover' thumbnail/>}
        </div>
        <div className={classes.commentsContainer}>
          <div className={classes.comments}>
            <CommentIcon />
            <span className={classes.commentsText}>{`${activity.comment_count} ${pluralize('Comment', activity.comment_count)}`}</span>
          </div>
          <div className={classes.readBy}>
            <Checkmark />
            <span className={classes.readByText}>{`Read by ${activity.like_count || 0}`}</span>
          </div>
        </div>
      </div>
      {editModal && <PostAnnouncementModal announcement={activity} onClose={handleCloseEditModal}
        isProgram={isProgram} isOpen={editModal} isEdit removeActivity={removeActivity} programsText={programsText}/>}
      <ConfirmationModal
        showConfirmationModal={confirmationModal}
        closeConfirmationModal={closeConfirmationModal}
        confirmationMessage={confirmationMesssage}
        confirmationDenied={closeConfirmationModal}
        confirmationProceed={deleteAnnouncement}
      />
      {modalOpen && <ActivityAnnouncementModal
        isOpen={modalOpen}
        onClose={onClose}
        activityId={activity.id}
        text={programsText} />}
    </>
  )
}

ActivityAnnouncement.propTypes = {
  activity: PropTypes.shape({
    id: PropTypes.number.isRequired,
    caption: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    activity_date: PropTypes.string.isRequired,
    photo: PropTypes.shape({
      original_file_url: PropTypes.string,
    }),
    document: PropTypes.string,
    video: PropTypes.shape({
      attachment_url: PropTypes.string,
    }),
    courses: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
      })
    ),
    series: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
      })
    ),
    users: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
      })
    ),
    comment_count: PropTypes.number.isRequired,
    like_count: PropTypes.number,
    user: PropTypes.object.isRequired,
  }).isRequired,
  removeActivity: PropTypes.func.isRequired,
  isProgram: PropTypes.bool.isRequired,
}

export default ActivityAnnouncement
